import classnames from 'classnames';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import isFunction from 'lodash-es/isFunction';
import map from 'lodash-es/map';
import PropTypes from 'prop-types';
import React, { forwardRef, memo, useEffect } from 'react';
import Checkbox from 'semantic-ui-react/dist/commonjs/modules/Checkbox';
import Loading from '../Loading';

import './SimpleGridTable.less';
const fullSpanTD = {
	gridColumnEnd: 42, // random "big" number
		gridColumnStart: 1,
		textAlign: 'center',
		width: '100%'
};
const [hseTgrid, hseTgridBody, hseTgridCell, hseTgridHeader, hseTgridRow] = [
	'hse-tgrid',
	'hse-tgrid-body',
	'hse-tgrid-cell',
	'hse-tgrid-header',
	'hse-tgrid-row'];

export const formatters = {
	YesNo: (object, columnConf, value) => (value ? 'Y' : 'N')
};

const FalseClick = () => false;
const renderHeader = ({ key, header, className }) => (
	<th className={classnames(hseTgridCell, className)} key={key}>
		{header === undefined ? key : header || ''}
	</th>
);

const renderColumn = ({ columns }) => {
	return row => {
		return map(columns, ({ key, formatter, content, className, ...props }) => {
			if (content) {
				return <td className={classnames(hseTgridCell, className)} key={key}>
					{content(row, {
						key,
						className, ...props
					})}</td>;
			}

			const formattedContent = isFunction(formatter)
				? formatter(
					row,
					{
						key,
						formatter,
						className,
						...props
					},
					key ? get(row, key) : undefined
				)
				: get(row, key);

			return (
				<td className={classnames(hseTgridCell, className)} key={key}>
					{formattedContent}
				</td>
			);
		});
	};
};
const createSelectBox = (onSelect, getRowId) => ({ row }) => (
	<Checkbox
		className="hse-tgrid-select"
		data-id={getRowId(row)}
		style={{ verticalAlign: 'middle' }}
		onChange={(e) => {
			e.stopPropagation();
			if (onSelect) onSelect(row, e);
			return false;
		}}
	/>
);
const SimpleGridTableBody = memo(
	props => {
		const { selectable, rowClass, rowClick, items, rowId, noData } = {noData: 'No Data',...props};
		const getRowId = isFunction(rowId) ? rowId : row => get(row, rowId || 'id');
		const configuredRenderColumn = renderColumn(props);
		const SelectBox = selectable && createSelectBox(selectable.onSelect, getRowId);
		return (
			<tbody className={hseTgridBody}>
			{items && items.length > 0 ?
				map(items, row => (
					<tr
						className={classnames(hseTgridRow, rowClass && rowClass(row))}
						onClick={rowClick ? () => rowClick(row) : FalseClick}
						key={getRowId(row)}
					>
						{selectable ? (
							<td className={classnames(hseTgridCell, selectable.className)}>
								<SelectBox row={row}/>
							</td>
						) : null}
						{configuredRenderColumn(row)}
					</tr>
				)) :
				<tr className={hseTgridRow}>
					<td className={hseTgridCell} style={fullSpanTD}>No data available</td>
				</tr>
			}
			</tbody>
		);
	},
	({ items: oldD }, { items: newD }) => oldD.length === newD.length && isEqual(oldD, newD)
);

const SimpleGridTable = forwardRef(({ id, fetch, isLoading, style, ...props }, ref) => {
	const { selectable, columns } = props;
	useEffect(() => {
		if (fetch) fetch();
	}, []);
	return (
		<table
			ref={ref}
			className={hseTgrid}
			id={id}
			style={{
				...style,
				gridTemplateColumns:
					(selectable ? '54px ' : '') + map(columns, ({ fr }) => fr ? `${fr}fr` : '1fr')
						.join(' ')
			}}
		>
			<thead className={hseTgridHeader}>
			<tr className={hseTgridRow}>
				{selectable
					? renderHeader({
						key: 'h-select',
						header: selectable.header
					})
					: null}
				{map(columns, renderHeader)}
			</tr>
			</thead>
			{isLoading ? <tbody>
			<tr>
				<td style={fullSpanTD}><Loading isLoading/></td>
			</tr>
			</tbody> : <SimpleGridTableBody {...props} />}
		</table>
	);
});

SimpleGridTable.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
	fetch: PropTypes.func.isRequired,
	id: PropTypes.string.isRequired,
	selectable: PropTypes.bool
};

export default SimpleGridTable;
